import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Trash2, RotateCcw, RefreshCw, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { translateError } from '@/crm/utils/translateError';
import { crmStore } from '../store';
import { useConfirm } from '../components/ConfirmDialog';

interface DeletedItem {
  id: string;
  label: string;
  subtitle?: string;
  deleted_at: string;
  raw: any;
}

type Resource = 'prestations' | 'clients' | 'artistes' | 'factures';

const RESOURCE_CONFIG: Record<Resource, { label: string; selectFields: string; mapper: (r: any) => DeletedItem }> = {
  prestations: {
    label: 'Prestations',
    selectFields: 'id, numero, client_nom, libelle, date_event, deleted_at',
    mapper: (r) => ({
      id: r.id,
      label: r.numero || '—',
      subtitle: [r.client_nom, r.libelle, r.date_event].filter(Boolean).join(' • '),
      deleted_at: r.deleted_at,
      raw: r,
    }),
  },
  clients: {
    label: 'Clients',
    selectFields: 'id, nom, email, tel, deleted_at',
    mapper: (r) => ({
      id: r.id,
      label: r.nom || '—',
      subtitle: [r.email, r.tel].filter(Boolean).join(' • '),
      deleted_at: r.deleted_at,
      raw: r,
    }),
  },
  artistes: {
    label: 'Artistes',
    selectFields: 'id, nom_scene, email, tel, deleted_at',
    mapper: (r) => ({
      id: r.id,
      label: r.nom_scene || '—',
      subtitle: [r.email, r.tel].filter(Boolean).join(' • '),
      deleted_at: r.deleted_at,
      raw: r,
    }),
  },
  factures: {
    label: 'Factures',
    selectFields: 'id, numero, client_nom, date_emission, deleted_at',
    mapper: (r) => ({
      id: r.id,
      label: r.numero || '—',
      subtitle: [r.client_nom, r.date_emission].filter(Boolean).join(' • '),
      deleted_at: r.deleted_at,
      raw: r,
    }),
  },
};

export default function Corbeille() {
  const [tab, setTab] = useState<Resource>('prestations');
  const confirm = useConfirm();
  const [items, setItems] = useState<Record<Resource, DeletedItem[]>>({
    prestations: [], clients: [], artistes: [], factures: [],
  });
  const [loading, setLoading] = useState(false);

  const loadAll = async () => {
    setLoading(true);
    const results = await Promise.all(
      (Object.keys(RESOURCE_CONFIG) as Resource[]).map(async (res) => {
        const cfg = RESOURCE_CONFIG[res];
        const { data, error } = await supabase
          .from(res)
          .select(cfg.selectFields)
          .not('deleted_at', 'is', null)
          .order('deleted_at', { ascending: false });
        if (error) {
          console.error(`[Corbeille] ${res}`, error);
          return [res, [] as DeletedItem[]] as const;
        }
        return [res, (data || []).map(cfg.mapper)] as const;
      })
    );
    const next: any = {};
    results.forEach(([res, list]) => { next[res] = list; });
    setItems(next);
    setLoading(false);
  };

  useEffect(() => { loadAll(); }, []);

  const restore = async (res: Resource, item: DeletedItem) => {
    const { error } = await supabase.from(res).update({ deleted_at: null }).eq('id', item.id);
    if (error) { toast.error('Restauration impossible : ' + translateError(error.message)); return; }
    toast.success(`« ${item.label} » restauré`);
    await loadAll();
    crmStore.reload();
  };

  const hardDelete = async (res: Resource, item: DeletedItem) => {
    const ok = await confirm({
      title: 'Supprimer définitivement ?',
      description: `« ${item.label} »\n\nCette action est irréversible.`,
      confirmLabel: 'Supprimer définitivement',
      destructive: true,
    });
    if (!ok) return;
    const { error } = await supabase.from(res).delete().eq('id', item.id);
    if (error) { toast.error('Suppression impossible : ' + translateError(error.message)); return; }
    toast.success(`« ${item.label} » supprimé définitivement`);
    await loadAll();
  };

  const emptyAll = async (res: Resource) => {
    const list = items[res];
    if (list.length === 0) return;
    const ok = await confirm({
      title: `Vider la corbeille « ${RESOURCE_CONFIG[res].label} » ?`,
      description: `${list.length} élément${list.length > 1 ? 's' : ''} seront supprimés. Action irréversible.`,
      confirmLabel: 'Vider',
      destructive: true,
    });
    if (!ok) return;
    const { error } = await supabase.from(res).delete().not('deleted_at', 'is', null);
    if (error) { toast.error('Échec : ' + translateError(error.message)); return; }
    toast.success('Corbeille vidée');
    await loadAll();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading text-amber-400 flex items-center gap-2">
            <Trash2 size={24} strokeWidth={1.5} /> Corbeille
          </h1>
          <p className="text-sm text-slate-400 mt-1">
            Restaurez les éléments supprimés ou videz définitivement la corbeille.
          </p>
        </div>
        <Button onClick={loadAll} disabled={loading} variant="outline" className="bg-slate-900 border-slate-700 hover:bg-slate-800">
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Actualiser
        </Button>
      </div>

      <Tabs value={tab} onValueChange={(v) => setTab(v as Resource)}>
        <TabsList className="bg-slate-900 border border-slate-800">
          {(Object.keys(RESOURCE_CONFIG) as Resource[]).map((res) => (
            <TabsTrigger key={res} value={res} className="data-[state=active]:bg-amber-500/10 data-[state=active]:text-amber-400">
              {RESOURCE_CONFIG[res].label}
              {items[res].length > 0 && (
                <span className="ml-2 text-[10px] bg-slate-700/60 text-slate-300 px-2 py-0.5 rounded-full">
                  {items[res].length}
                </span>
              )}
            </TabsTrigger>
          ))}
        </TabsList>

        {(Object.keys(RESOURCE_CONFIG) as Resource[]).map((res) => (
          <TabsContent key={res} value={res} className="mt-4">
            <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-800 flex items-center justify-between">
                <div className="text-sm text-slate-300">
                  {items[res].length} élément{items[res].length > 1 ? 's' : ''} dans la corbeille
                </div>
                {items[res].length > 0 && (
                  <Button onClick={() => emptyAll(res)} variant="outline" size="sm" className="border-red-500/40 text-red-400 hover:bg-red-500/10">
                    <Trash2 size={13} /> Vider la corbeille
                  </Button>
                )}
              </div>
              {items[res].length === 0 ? (
                <div className="px-6 py-12 text-center text-slate-500 text-sm">
                  Aucun élément supprimé.
                </div>
              ) : (
                <div className="overflow-x-auto"><table className="w-full text-sm min-w-[640px]">
                  <thead className="bg-slate-950/60 text-xs uppercase text-slate-400 border-b border-slate-800">
                    <tr>
                      <th className="px-4 py-3 text-left">Référence</th>
                      <th className="px-4 py-3 text-left">Détails</th>
                      <th className="px-4 py-3 text-left">Supprimé le</th>
                      <th className="px-4 py-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items[res].map((it) => (
                      <tr key={it.id} className="border-b border-slate-800/60 hover:bg-slate-800/30">
                        <td className="px-4 py-3 text-slate-200 font-medium">{it.label}</td>
                        <td className="px-4 py-3 text-slate-400 text-xs max-w-md truncate">{it.subtitle || '—'}</td>
                        <td className="px-4 py-3 text-slate-400 text-xs whitespace-nowrap">
                          {new Date(it.deleted_at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })}
                        </td>
                        <td className="px-4 py-3 text-right space-x-2 whitespace-nowrap">
                          <Button onClick={() => restore(res, it)} size="sm" variant="outline" className="border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10">
                            <RotateCcw size={13} /> Restaurer
                          </Button>
                          <Button onClick={() => hardDelete(res, it)} size="sm" variant="outline" className="border-red-500/40 text-red-400 hover:bg-red-500/10">
                            <Trash2 size={13} />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table></div>
              )}
            </div>
          </TabsContent>
        ))}
      </Tabs>

      <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-4 flex gap-3 text-xs text-amber-200/80">
        <AlertTriangle size={16} className="shrink-0 text-amber-400 mt-0.5" />
        <div>
          La suppression définitive est irréversible. Les éléments restaurés réapparaîtront immédiatement dans leur module d'origine.
        </div>
      </div>
    </div>
  );
}
